import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Preferences } from '@capacitor/preferences';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Share } from '@capacitor/share';
import type { AppData } from '@/utils/storage';

// Function to safely check if we're in a native environment
const isNativeEnvironment = async (): Promise<boolean> => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Native Features Service
 * Provides haptic feedback, status bar control, native storage, and app state management with graceful web fallbacks
 */
export class NativeFeatures {
  private static appStateCallback: ((isActive: boolean) => void) | null = null;

  /**
   * Initialize native features and set up app state listeners
   */
  static async initialize(): Promise<void> {
    try {
      if (await isNativeEnvironment()) {
        // Set up app state change listener using Capacitor's App plugin
        const { App } = await import('@capacitor/app');
        
        App.addListener('appStateChange', ({ isActive }) => {
          console.log('App state changed. Is active:', isActive);
          if (this.appStateCallback) {
            this.appStateCallback(isActive);
          }
        });

        console.log('Native features initialized');
      } else {
        console.log('Running in web environment - native features will use fallbacks');
      }
    } catch (error) {
      console.debug('Error initializing native features:', error);
    }
  }

  /**
   * Set callback for app state changes
   * @param callback - Function to call when app state changes
   */
  static setAppStateChangeCallback(callback: (isActive: boolean) => void): void {
    this.appStateCallback = callback;
  }

  /**
   * Save data to native storage
   * @param key - Storage key
   * @param data - Data to save
   */
  static async saveToNativeStorage(key: string, data: AppData): Promise<void> {
    try {
      if (await isNativeEnvironment()) {
        const dataString = JSON.stringify(data);
        await Preferences.set({
          key,
          value: dataString
        });
        console.log(`Data saved to native storage with key: ${key}`);
      } else {
        // Fallback to localStorage on web
        const dataString = JSON.stringify(data);
        localStorage.setItem(key, dataString);
        console.log(`Data saved to localStorage (web fallback) with key: ${key}`);
      }
    } catch (error) {
      console.error('Error saving to native storage:', error);
      throw error;
    }
  }

  /**
   * Get data from native storage
   * @param key - Storage key
   * @returns Parsed data or null if not found
   */
  static async getFromNativeStorage(key: string): Promise<AppData | null> {
    try {
      if (await isNativeEnvironment()) {
        const result = await Preferences.get({ key });
        if (result.value) {
          return JSON.parse(result.value);
        }
      } else {
        // Fallback to localStorage on web
        const localData = localStorage.getItem(key);
        if (localData) {
          return JSON.parse(localData);
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting from native storage:', error);
      throw error;
    }
  }
  /**
   * Trigger success haptic feedback
   * Used for positive actions like task completion or reward claiming
   */
  static async hapticSuccess(): Promise<void> {
    try {
      if (await isNativeEnvironment()) {
        await Haptics.notification({ type: NotificationType.Success });
      }
    } catch (error) {
      // Silently fail on web or if haptics not available
      console.debug('Haptic success feedback not available:', error);
    }
  }

  /**
   * Trigger warning haptic feedback
   * Used for warning situations like insufficient stars
   */
  static async hapticWarning(): Promise<void> {
    try {
      if (await isNativeEnvironment()) {
        await Haptics.notification({ type: NotificationType.Warning });
      }
    } catch (error) {
      // Silently fail on web or if haptics not available
      console.debug('Haptic warning feedback not available:', error);
    }
  }

  /**
   * Trigger impact haptic feedback
   * Used for general interactions and celebrations
   */
  static async hapticImpact(): Promise<void> {
    try {
      if (await isNativeEnvironment()) {
        await Haptics.impact({ style: ImpactStyle.Medium });
      }
    } catch (error) {
      // Silently fail on web or if haptics not available
      console.debug('Haptic impact feedback not available:', error);
    }
  }

  /**
   * Set status bar theme based on app theme
   * @param isDarkTheme - Whether the current theme is dark
   */
  static async setStatusBarTheme(isDarkTheme: boolean): Promise<void> {
    try {
      if (await isNativeEnvironment()) {
        // For dark themes, use light status bar content
        // For light themes, use dark status bar content
        const style = isDarkTheme ? Style.Light : Style.Dark;
        await StatusBar.setStyle({ style });
      }
    } catch (error) {
      // Silently fail on web or if status bar control not available
      console.debug('Status bar control not available:', error);
    }
  }

  /**
   * Request notification permissions
   * @returns Permission status
   */
  static async requestNotificationPermissions(): Promise<boolean> {
    try {
      if (await isNativeEnvironment()) {
        const result = await LocalNotifications.requestPermissions();
        return result.display === 'granted';
      }
      // Web fallback - check if Notification API is available
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
      }
      return false;
    } catch (error) {
      console.debug('Notification permissions not available:', error);
      return false;
    }
  }

  /**
   * Schedule a local notification
   * @param title - Notification title
   * @param body - Notification body
   * @param id - Unique notification ID
   * @param scheduleAt - When to show the notification (optional, defaults to now)
   */
  static async scheduleNotification(
    title: string,
    body: string,
    id: number,
    scheduleAt?: Date
  ): Promise<void> {
    try {
      if (await isNativeEnvironment()) {
        await LocalNotifications.schedule({
          notifications: [
            {
              title,
              body,
              id,
              schedule: scheduleAt ? { at: scheduleAt } : undefined,
            }
          ]
        });
      } else {
        // Web fallback using Notification API
        if ('Notification' in window && Notification.permission === 'granted') {
          if (scheduleAt) {
            const delay = scheduleAt.getTime() - Date.now();
            if (delay > 0) {
              setTimeout(() => {
                new Notification(title, { body });
              }, delay);
            }
          } else {
            new Notification(title, { body });
          }
        }
      }
    } catch (error) {
      console.debug('Failed to schedule notification:', error);
    }
  }

  /**
   * Cancel a scheduled notification
   * @param id - Notification ID to cancel
   */
  static async cancelNotification(id: number): Promise<void> {
    try {
      if (await isNativeEnvironment()) {
        await LocalNotifications.cancel({
          notifications: [{ id }]
        });
      }
      // Web doesn't have a direct way to cancel scheduled notifications
    } catch (error) {
      console.debug('Failed to cancel notification:', error);
    }
  }

  /**
   * Share content using native share dialog
   * @param title - Share title
   * @param text - Share text
   * @param url - Optional URL to share
   */
  static async shareContent(title: string, text: string, url?: string): Promise<boolean> {
    try {
      if (await isNativeEnvironment()) {
        await Share.share({
          title,
          text,
          url,
        });
        return true;
      } else {
        // Web fallback using Web Share API or clipboard
        if (navigator.share) {
          await navigator.share({
            title,
            text,
            url,
          });
          return true;
        } else if (navigator.clipboard) {
          const shareText = url ? `${text}\n${url}` : text;
          await navigator.clipboard.writeText(shareText);
          console.log('Content copied to clipboard');
          return true;
        }
        return false;
      }
    } catch (error) {
      console.debug('Failed to share content:', error);
      return false;
    }
  }
}